// frontend/modules/customer-notification/components/trigger/MarketingNotificationTrigger.tsx
import React, { useState } from 'react';
import { Card, Form, Input, Select, Button, Alert, DatePicker, Radio, Space, Divider, Typography, Result, Spin, Collapse } from 'antd';
import { SendOutlined, GiftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNotificationTrigger } from '../../hooks/useNotificationTrigger';
import { NotificationPlatforms } from '@/shared/api/model';
import CustomerSegmentSelector from '../common/CustomerSegmentSelector';
import NotificationTemplateSelector from '../common/NotificationTemplateSelector';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

interface MarketingNotificationTriggerProps {
    onClose: () => void;
}

const MarketingNotificationTrigger: React.FC<MarketingNotificationTriggerProps> = ({ onClose }) => {
    const [form] = Form.useForm();
    const [notificationType, setNotificationType] = useState<'promotional' | 'product-change' | 'meeting'>('promotional');
    const [customerFilters, setCustomerFilters] = useState<Record<string, any>>({});
    const { 
        isLoading, 
        sendPromotionalNotifications, 
        sendProductChangeNotifications, 
        sendGeneralMeetingNotifications,
        resultSummary 
    } = useNotificationTrigger();
    
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            switch (notificationType) {
                case 'promotional':
                    await sendPromotionalNotifications(
                        values.templateName,
                        values.platform,
                        customerFilters,
                        values.templateParameters ? JSON.parse(values.templateParameters) : {}
                    );
                    break;
                
                case 'product-change':
                    await sendProductChangeNotifications(
                        values.templateName,
                        customerFilters,
                        values.productName,
                        values.effectiveDate.toDate(),
                        values.changeDetails
                    );
                    break;
                
                case 'meeting':
                    await sendGeneralMeetingNotifications(
                        values.meetingDate.toDate(),
                        values.meetingTime,
                        values.location,
                        values.agenda,
                        customerFilters
                    );
                    break;
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };
    
    const handleCustomerFiltersChange = (filters: Record<string, any>) => {
        setCustomerFilters(filters);
    };
    
    // Show success result if we have sent notifications
    if (resultSummary) {
        return (
            <Card>
                <Result
                    status="success"
                    title="Marketing Notifications Sent Successfully"
                    subTitle={`${resultSummary.total} notifications sent at ${new Date(resultSummary.timestamp).toLocaleString()}`}
                    icon={<CheckCircleOutlined />}
                    extra={[
                        <Button type="primary" key="done" onClick={onClose}>
                            Done
                        </Button>
                    ]}
                />
            </Card>
        );
    }
    
    return (
        <Card title={
            <Space>
                <GiftOutlined />
                <span>Marketing Notifications</span>
            </Space>
        }>
            {isLoading ? (
                <div className="text-center py-12">
                    <Spin size="large" />
                    <div className="mt-4">
                        <Text>Processing marketing notifications...</Text>
                    </div>
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        platform: NotificationPlatforms.SMS
                    }}
                >
                    <Alert
                        message="Marketing Notification Trigger"
                        description="Send promotional messages, product change notifications, or meeting invitations to your customers."
                        type="info"
                        showIcon
                        className="mb-6"
                    />
                    
                    <Form.Item label="Notification Type">
                        <Radio.Group 
                            value={notificationType} 
                            onChange={(e) => setNotificationType(e.target.value)}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="promotional">Promotional</Radio.Button>
                            <Radio.Button value="product-change">Product Change</Radio.Button>
                            <Radio.Button value="meeting">General Meeting</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    
                    <Divider orientation="left">Message Content</Divider>
                    
                    {notificationType === 'promotional' && (
                        <>
                            <Form.Item
                                name="templateName"
                                label="Template Name"
                                rules={[{ required: true, message: 'Please select a template' }]}
                            >
                                <NotificationTemplateSelector />
                            </Form.Item>
                            
                            <Form.Item
                                name="platform"
                                label="Platform"
                                rules={[{ required: true, message: 'Please select a platform' }]}
                            >
                                <Select>
                                    <Option value={NotificationPlatforms.SMS}>SMS</Option>
                                    <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
                                    <Option value={NotificationPlatforms.EMAIL}>Email</Option>
                                </Select>
                            </Form.Item>
                            
                            <Form.Item
                                name="templateParameters"
                                label="Template Parameters (JSON)"
                                tooltip="Enter parameters as JSON object, e.g. {'key': 'value'}"
                            >
                                <TextArea 
                                    rows={4} 
                                    placeholder='{"param1": "value1", "param2": "value2"}'
                                />
                            </Form.Item>
                        </>
                    )}
                    
                    {notificationType === 'product-change' && (
                        <>
                            <Form.Item
                                name="templateName"
                                label="Template Name"
                                rules={[{ required: true, message: 'Please select a template' }]}
                            >
                                <NotificationTemplateSelector />
                            </Form.Item>
                            
                            <Form.Item
                                name="productName"
                                label="Product Name"
                                rules={[{ required: true, message: 'Please enter product name' }]}
                            >
                                <Input placeholder="e.g. Business Loan, Savings Account" />
                            </Form.Item>
                            
                            <Form.Item
                                name="effectiveDate"
                                label="Effective Date"
                                rules={[{ required: true, message: 'Please select effective date' }]}
                            >
                                <DatePicker className="w-full" />
                            </Form.Item>
                            
                            <Form.Item
                                name="changeDetails"
                                label="Change Details"
                                rules={[{ required: true, message: 'Please enter change details' }]}
                            >
                                <TextArea 
                                    rows={4} 
                                    placeholder="Describe the changes being made to the product..."
                                />
                            </Form.Item>
                        </>
                    )}
                    
                    {notificationType === 'meeting' && (
                        <>
                            <Form.Item
                                name="meetingDate"
                                label="Meeting Date"
                                rules={[{ required: true, message: 'Please select meeting date' }]}
                            >
                                <DatePicker className="w-full" />
                            </Form.Item>
                            
                            <Form.Item
                                name="meetingTime"
                                label="Meeting Time"
                                rules={[{ required: true, message: 'Please enter meeting time' }]}
                            >
                                <Input placeholder="e.g. 2:00 PM" />
                            </Form.Item>
                            
                            <Form.Item
                                name="location"
                                label="Location"
                                rules={[{ required: true, message: 'Please enter meeting location' }]}
                            >
                                <Input placeholder="e.g. Main Branch Conference Room" />
                            </Form.Item>
                            
                            <Form.Item
                                name="agenda"
                                label="Agenda"
                                rules={[{ required: true, message: 'Please enter meeting agenda' }]}
                            >
                                <TextArea 
                                    rows={4} 
                                    placeholder="Brief description of meeting topics..."
                                />
                            </Form.Item>
                        </>
                    )}
                    
                    <Divider orientation="left">Target Audience</Divider>
                    
                    <Collapse defaultActiveKey={['1']} className="mb-6">
                        <Panel header="Customer Segment Filters" key="1">
                            <CustomerSegmentSelector onChange={handleCustomerFiltersChange} />
                        </Panel>
                    </Collapse>
                    
                    <Form.Item className="mb-0">
                        <Space>
                            <Button type="primary" onClick={handleSubmit} icon={<SendOutlined />}>
                                Send Notifications
                            </Button>
                            <Button onClick={onClose}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            )}
        </Card>
    );
};

export default MarketingNotificationTrigger;
